// Modal „Možnosti doručení" na detailu produktu — náhrada nenastylovaného
// nativního popupu (Shoptet ho otevírá v colorboxu ze stránky
// `/<slug>:moznosti-dopravy/`).
//
// Nativní popup ukazuje plochý seznam metod bez termínu; zákazník pak v košíku
// vidí něco jiného než na detailu. Modal proto skládá TYTÉŽ informace jako krok 2
// pokladny: skupiny (výdejní místa / adresa / osobní odběr) ze sdíleného
// `shippingModel.js`, loga, ceny, popisy metod z nativních tooltipů, termín
// doručení a pás dopravy zdarma s rozsahem „na výdejní místo".
//
// Nabídka je PRODUCT-SCOPED (zadání Tomáše 2026-09-01): fetch stránky možností
// jede bez cookies (shippingModel.js), takže obsah košíku nabídku na detailu
// neovlivní — nadrozměr v košíku dřív uměl na detailu běžného produktu nechat
// jen atypickou přepravu za 149 Kč. Pás dopravy zdarma naopak ZŮSTÁVÁ vázaný
// na košík: jde z dataLayeru `cartInfo` (benefitBand.js), stejná čísla jako
// progress výhod v košíku a pokladně.
//
// Zdroje pravdy (nic se nedopočítává):
//   • metody, ceny, loga, popisy = nativní stránka možností dopravy pro produkt
//     (fetch sdílený s řádkem „Doprava od X Kč", shippingModel.js)
//   • postup k dopravě zdarma = dataLayer `cartInfo` (benefitBand.js)
//   • den doručení = nativní `div.delivery-time` téhle stránky, přes
//     `deliveryTimeline.js` (tentýž zdroj, jaký kreslí časovou osu v nákupní kartě)
//   • expedice a countdown = uzávěrka 15:00, v pátek 14:30, se svátky (`dispatchEta.js`)
// Termín per dopravce Shoptet nezná (CLAUDE.md „Termín doručení") — modal ho
// proto drží na úrovni skupiny. Osobní odběr nativní datum NEBERE (je to termín
// kurýra, ne termín připravení na prodejně): jeho termín je nejbližší otevírací
// den prodejny (`nextPickupDay`), psaný stejným tvarem a stejně zeleně jako
// termín doručení (zadání Tomáše 2026-08-31).
//
// Skupina s JEDINOU metodou nemá podseznam — logo, cena i popis metody se
// skládají rovnou do hlavičky (podvýběr, kde není z čeho vybírat, mátl; totéž
// pravidlo drží krok 2 pokladny). Logo osobního odběru (značka shopu) ani tag
// „Nejlevnější" se nekreslí vůbec.
//
// Mechanika: klik na `a.shipping-options` chytáme v CAPTURE fázi se
// stopPropagation (default JS by jinak otevřel colorbox). Rám dialogu a jeho
// chování (scrim, ESC, focus trap, mobilní sheet se swipe-down a Android zpět)
// dodává od 2026-09-02 sdílený `modalShell.js` (vytažený odsud, když přibyl
// modal servisu) — tenhle modul staví jen OBSAH těla. Modal košíku
// (`cartModal.js`) zatím jede na vlastní kopii chování.

import { icon } from './icon.js';
import { createModal, shellNode, shellClass } from './modalShell.js';
import { carrierLogo } from './logo.js';
import { ensureLogos } from './injectSprite.js';
import {
  deliveryTimeline,
  parseNativeDate,
  countdownLabel,
  relativeDayLabel,
  fastCarrier,
  fastOffered,
  fastFirst,
  fastPromo,
  LABEL_FASTEST,
} from './deliveryTimeline.js';
import { freeShippingBand } from './benefitBand.js';
import { shoptetData } from '../shoptet-hooks.js';
import {
  SHIP_GROUPS,
  OVERSIZE_NOTE_PRODUCT,
  PICKUP_ADDRESS,
  PICKUP_MAP_URL,
  PICKUP_MAP_LABEL,
  PICKUP_STOCK_NOTE,
  PICKUP_HOURS_LABEL,
  FREE_SHIP_FROM_FALLBACK,
  FREE_SHIPPING,
  HAS_WEIGHT_BANDS,
  WEIGHT_PRODUCT_A,
  WEIGHT_PRODUCT_B,
  WEIGHT_PRODUCT_SUM,
  WEIGHT_PRODUCT_FREE_A,
  WEIGHT_PRODUCT_FREE_B,
  pickupStatus,
  nextPickupDay,
  openingHours,
  isOnlyOversize,
  loadShippingOptions,
  productWeight,
  bandOf,
  formatKg,
  shippingPromo,
  isPromoRow,
} from './shippingModel.js';
import { page } from './pages.js';
import { money } from './money.js';
import L from '../locale/cs/shippingModal.js';

const PREFIX = 'bs-ship';
const TICK_MS = 30000;

const TITLE = L.title;
const SUB = L.sub;
const CLOSE_LABEL = L.close;
const DONE_LABEL = L.done;
const TERMS_LABEL = L.terms;
const TERMS_HREF = page('shipping');
const LABEL_DELIVER = L.deliver;
const LABEL_PICKUP_ETA = L.pickupEta;
const LABEL_SHIP = L.ship;
const LABEL_COUNTDOWN = L.countdown;
// dovětek pásu expedice v okně nedělního svozu PPL („Odešleme v pondělí 7. 9.
// · s PPL už zítra 6. 9."): pás mluví o běžné expedici skladu, rychlý dopravce
// má vlastní den odeslání
const LABEL_FAST_SHIP = L.fastShip;
const LABEL_LOADING = L.loading;
const LABEL_ERROR = L.error;

const PICKUP_GROUP = SHIP_GROUPS.find(function (g) {
  return g.key === 'pickup';
});

const node = shellNode;

// Odkaz na naše „Doprava a platba" bereme přednostně z existující navigace,
// ať se slug nerozejde s administrací.
function termsHref() {
  const link = document.querySelector('a[href*="doprava-platba"], a[href*="doprava-a-platba"]');
  return link ? link.getAttribute('href') : TERMS_HREF;
}

function nativeDelivery() {
  const box = document.querySelector('.delivery-time');
  return box ? parseNativeDate(box.textContent) : null;
}

export function initShippingModal() {
  // idempotence: modal visí na dokumentu, ne na uzlu detailu
  if (document.documentElement.dataset.bsShip) return;
  document.documentElement.dataset.bsShip = '1';

  let tick = null;
  const modal = createModal({
    prefix: PREFIX,
    icon: 'truck',
    title: TITLE,
    sub: SUB,
    closeLabel: CLOSE_LABEL,
    doneLabel: DONE_LABEL,
    link: { label: TERMS_LABEL, href: termsHref },
    onClose: function () {
      clearInterval(tick);
      tick = null;
    },
  });

  // --- stavba --------------------------------------------------------------

  // Logo dopravce. Osobní odběr ho NEDOSTÁVÁ — je to značka shopu a mezi
  // dopravci nenese informaci (zadání Tomáše 2026-08-31).
  //
  // Přednost má NAŠE logo ze spritu (vektor, jednotná optická výška); nativní
  // obrázek Shoptetu (101×50 px, u GLS dokonce PNG) zůstává fallbackem pro
  // dopravce, kterého sprite nezná.
  function buildLogo(row, def) {
    if (def && def.key === 'pickup') return null;
    const box = node('span', 'bs-ship__logo');
    const mark = carrierLogo(row.name);
    if (mark) {
      box.classList.add('is-vector');
      box.appendChild(mark);
    } else if (row.logo) {
      const img = node('img');
      img.src = row.logo;
      img.alt = row.name;
      img.loading = 'lazy';
      box.appendChild(img);
    }
    return box;
  }

  // Akce na dopravu (#322, mock M2): metoda za akční cenu (odpověď platformy,
  // `isPromoRow`) nese pod cenou přeškrtnutou cenu ceníku a v řádku názvu štítek
  // s koncem akce. Ostatní metody beze změny.
  function promoOf(row) {
    const pr = shippingPromo();
    return pr && isPromoRow(row, pr) ? pr : null;
  }

  function buildPrice(row) {
    const price = node('strong', 'bs-ship__price' + (row.free ? ' is-free' : ''), row.priceText);
    const pr = promoOf(row);
    if (!pr) return price;
    price.classList.add('is-promo');
    const box = node('span', 'bs-ship__pricebox');
    box.appendChild(price);
    box.appendChild(node('s', 'bs-ship__was', money(pr.was)));
    return box;
  }

  // `fastMode` = skupina má v okně zvláštního závozu metodu rychlého dopravce
  // (GLS sobota / PPL neděle, deliveryTimeline.js `t.fast`) → každá podvolba
  // nese vlastní termín (rychlý dopravce dřívější, ostatní běžný), aby bylo
  // vidět, ČÍM termín platí. Mimo okno podvolby termín nenesou (drží ho
  // hlavička skupiny). `mixed` = ve skupině je i metoda bez závozu → rychlý
  // dopravce dostane štítek „Nejrychlejší" (review Tomáše 2026-09-04); skupina
  // jen z jednoho dopravce štítek nemá, není proti čemu být nejrychlejší.
  function buildOption(row, def, t, fastMode, mixed) {
    const li = node('li', 'bs-ship__opt');

    const info = node('span', 'bs-ship__optinfo');
    // název + (v okně u rychlého dopravce) štítek „Nejrychlejší" v jednom řádku
    const head = node('span', 'bs-ship__opthead');
    head.appendChild(node('strong', 'bs-ship__optlabel', row.name));
    const isFast = fastMode && fastCarrier(t, row.name);
    if (isFast && mixed) head.appendChild(node('span', 'bs-ship__ctag bs-ship__fasttag', LABEL_FASTEST));
    const pr = promoOf(row);
    if (pr) head.appendChild(node('span', 'bs-ship__ctag bs-ship__promotag', L.promoTag(pr.untilLabel)));
    info.appendChild(head);
    if (row.desc) info.appendChild(node('span', 'bs-ship__optdesc', row.desc));
    if (fastMode && t) {
      const eta = node('span', 'bs-ship__opteta' + (isFast ? ' is-fast' : ''));
      eta.appendChild(icon('calendar'));
      eta.appendChild(node('span', 'bs-ship__opteta-txt', LABEL_DELIVER + ' ' + (isFast ? t.fast.deliver : t.deliver)));
      info.appendChild(eta);
    }
    li.appendChild(info);

    const logo = buildLogo(row, def);
    if (logo) li.appendChild(logo);
    li.appendChild(buildPrice(row));
    return li;
  }

  // Blok osobního odběru (zadání 2026-08-31): stav otevřeno/zavřeno s nejbližším
  // termínem vyzvednutí, poznámka o okamžitém odběru skladového zboží, adresa
  // a odkaz na mapu (nové okno). Stav přerenderovává tick modalu (přelom 17:00).
  function renderPickupStatus(el) {
    const s = pickupStatus(new Date());
    el.classList.toggle('is-open', s.open);
    el.textContent = '';
    el.appendChild(node('span', 'bs-ship__pickdot'));
    el.appendChild(node('span', null, s.text));
  }

  // Kompletní otevírací doba pod adresou (zadání Tomáše 2026-08-31) — skládá ji
  // `openingHours()` z jediného zdroje hodin na webu (CONTACT v headerTools.js).
  function buildPickupHours() {
    const row = node('span', 'bs-ship__pickrow bs-ship__pickhours');
    row.appendChild(icon('clock'));

    const box = node('span', 'bs-ship__hours');
    box.appendChild(node('span', 'bs-ship__hourslabel', PICKUP_HOURS_LABEL));
    const list = node('span', 'bs-ship__hourslist');
    openingHours(new Date()).forEach(function (h) {
      const item = node(
        'span',
        'bs-ship__hoursrow' + (h.today ? ' is-today' : '') + (h.open ? '' : ' is-closed')
      );
      item.appendChild(node('span', 'bs-ship__hoursday', h.label));
      item.appendChild(node('span', 'bs-ship__hoursval', h.value));
      list.appendChild(item);
    });
    box.appendChild(list);
    row.appendChild(box);
    return row;
  }

  function buildPickupInfo() {
    const box = node('div', 'bs-ship__pickup');

    const status = node('span', 'bs-ship__pickrow bs-ship__pickstatus');
    renderPickupStatus(status);
    box.appendChild(status);

    const stock = node('span', 'bs-ship__pickrow');
    stock.appendChild(icon('check'));
    stock.appendChild(node('span', null, PICKUP_STOCK_NOTE));
    box.appendChild(stock);

    const addr = node('span', 'bs-ship__pickrow');
    addr.appendChild(icon('pin'));
    const addrTxt = node('span', null, PICKUP_ADDRESS + ' · ');
    const map = node('a', 'bs-ship__maplink', PICKUP_MAP_LABEL);
    map.href = PICKUP_MAP_URL;
    map.target = '_blank';
    map.rel = 'noopener noreferrer';
    addrTxt.appendChild(map);
    addr.appendChild(addrTxt);
    box.appendChild(addr);

    box.appendChild(buildPickupHours());
    return box;
  }

  // Má skupina v okně zvláštního závozu metodu rychlého dopravce?
  // (deliveryTimeline.js `t.fast`)
  function groupFast(def, rows, t) {
    return (
      !!(t && t.fast && def.eta) &&
      fastOffered(
        t,
        (rows || []).map(function (r) {
          return r.name;
        })
      )
    );
  }

  // Termín skupiny. Doručované nesou nativní den doručení (jeden pro celý
  // košík), osobní odběr nejbližší den, kdy má prodejna otevřeno — stejný tvar
  // i stejná zelená jako u dopravců (zadání Tomáše 2026-08-31). V okně
  // zvláštního závozu nese skupina s rychlým dopravcem nejbližší termín svých
  // metod (sobotu GLS / pondělí PPL); KTERÝ dopravce to je, říká řádek metody
  // se svým logem — hlavička nese jen datum (zadání Tomáše 2026-09-05: dovětek
  // s logem stál u nadrozměru hned vedle loga metody, tedy dvakrát za sebou).
  function renderEta(cell, def, t, rows) {
    let label = '';
    if (def.key === 'pickup') {
      label = LABEL_PICKUP_ETA + ' ' + relativeDayLabel(nextPickupDay(new Date()));
    } else if (def.eta && t) {
      const fast = groupFast(def, rows, t);
      label = LABEL_DELIVER + ' ' + (fast ? t.fast.deliver : t.deliver);
    }
    cell.textContent = '';
    if (!label) return false;
    // ikona kalendáře u každého termínu (zadání Tomáše 2026-09-04)
    cell.appendChild(icon('calendar'));
    cell.appendChild(document.createTextNode(label));
    return true;
  }

  // Skupina s JEDINOU metodou se skládá rovnou do hlavičky — podvýběr, kde není
  // z čeho vybírat, jen mátl (zadání Tomáše 2026-08-31; totéž pravidlo drží
  // krok 2 pokladny). Týká se osobního odběru i nabídky zúžené na nadrozměrnou
  // přepravu; logo, cena a popis metody se stěhují na hlavní řádek, ať se se
  // seznamem neztratí.
  function buildGroup(def, rows, t) {
    const sole = rows.length === 1 ? rows[0] : null;
    const pickup = def.key === 'pickup';
    const fastMode = groupFast(def, rows, t);
    const sec = node('section', 'bs-ship__grp' + (sole ? ' bs-ship__grp--single' : ''));
    sec.dataset.bsShipGroup = def.key;

    const head = node('header', 'bs-ship__grphead');
    const badge = node('span', 'bs-ship__grpicon');
    badge.appendChild(icon(def.icon));
    head.appendChild(badge);

    const txt = node('span', 'bs-ship__grptxt');
    txt.appendChild(node('strong', 'bs-ship__grptitle', def.title));
    // Jediná metoda: druhý řádek nese jméno dopravce (u nadrozměru je to jediná
    // informace, proč ostatní možnosti zmizely). Osobní odběr si nechává adresu
    // prodejny — „dopravcem" je shop sám a detaily nese blok pod skupinou.
    txt.appendChild(node('span', 'bs-ship__grpdesc', sole && !pickup ? sole.name : def.desc));
    if (sole && !pickup && sole.desc) txt.appendChild(node('span', 'bs-ship__grpnote', sole.desc));
    head.appendChild(txt);

    if (sole) {
      const logo = buildLogo(sole, def);
      if (logo) head.appendChild(logo);
    }
    const etaCell = node('span', 'bs-ship__grpeta');
    if (renderEta(etaCell, def, t, rows)) head.appendChild(etaCell);
    if (sole) head.appendChild(buildPrice(sole));
    sec.appendChild(head);

    if (!sole) {
      const list = node('ul', 'bs-ship__list');
      // v okně rychlý dopravce první (nejrychlejší volba nahoře), jinak nativní pořadí
      const ordered = fastMode
        ? fastFirst(t, rows, function (r) {
            return r.name;
          })
        : rows;
      const mixed =
        fastMode &&
        rows.some(function (r) {
          return !fastCarrier(t, r.name);
        });
      ordered.forEach(function (row) {
        list.appendChild(buildOption(row, def, t, fastMode, mixed));
      });
      sec.appendChild(list);
    }
    if (pickup) sec.appendChild(buildPickupInfo());
    return sec;
  }

  // Pás expedice nad skupinami: countdown do cut-offu, mimo něj den odeslání.
  function buildDispatch() {
    const zone = node('div', 'bs-ship__eta');
    zone.appendChild(icon('clock', 'bs-ship__etaicon'));
    const txt = node('span', 'bs-ship__etatxt');
    zone.appendChild(txt);
    return { zone: zone, txt: txt };
  }

  // Věta o hmotnosti produktu a pásmu ceníku (mock 2026-09-02 §4): „Produkt
  // váží 12,5 kg, ceny níže platí pro pásmo 10–20 kg. V košíku se hmotnost
  // položek sčítá; do 10 kg a nad 2 000 Kč je výdejní místo zdarma." Váha jde
  // z dataLayeru `product.weight` (u variant `codes[].weight`); bez ní věta
  // odpadá, nikdy se neodhaduje. Atypický produkt (bez výdejních míst
  // v nabídce) zmínku o dopravě zdarma nedostane. Práh: dataLayer košíku,
  // jinak zrcadlo ceníku (shippingModel.js FREE_SHIP_FROM_FALLBACK).
  function freeFromText() {
    const info = shoptetData('cartInfo');
    const n = Number(info && info.freeShippingFrom) || FREE_SHIP_FROM_FALLBACK;
    // pevné mezery — „2 000 Kč" se nesmí zalomit uprostřed částky (money())
    return money(n);
  }

  function buildWeightLine(rows) {
    // plochý tarif (SK): hmotnost o ceně nerozhoduje, věta by neměla co říct
    if (!HAS_WEIGHT_BANDS) return null;
    const product = shoptetData('product');
    const kg = productWeight(product, product && product.code);
    const band = bandOf(kg);
    if (!band) return null;
    const hasBox =
      FREE_SHIPPING &&
      rows.some(function (r) {
        return r.group === 'box';
      });
    const row = node('p', 'bs-ship__wline');
    row.appendChild(icon('scale'));
    const txt = node('span', 'bs-ship__wlinetxt');
    txt.appendChild(document.createTextNode(WEIGHT_PRODUCT_A));
    txt.appendChild(node('b', null, formatKg(kg)));
    txt.appendChild(document.createTextNode(WEIGHT_PRODUCT_B + band.label + '. ' + WEIGHT_PRODUCT_SUM));
    txt.appendChild(
      document.createTextNode(hasBox ? WEIGHT_PRODUCT_FREE_A + freeFromText() + WEIGHT_PRODUCT_FREE_B : '.')
    );
    row.appendChild(txt);
    return row;
  }

  // Klíč aktivního závozu (`sat` / `sun`), jen s dopravcem v nabídce; jinak null.
  function fastKeyFor(t, names) {
    return fastOffered(t, names) ? t.fast.key : null;
  }

  function renderDispatch(ui) {
    const t = deliveryTimeline(new Date(), nativeDelivery());
    ui.dispatch.txt.textContent = '';
    if (t.shipsToday) {
      ui.dispatch.txt.appendChild(document.createTextNode(L.orderToday(t.cutoff)));
      if (t.countdown) {
        ui.dispatch.txt.appendChild(document.createTextNode(' · ' + LABEL_COUNTDOWN + ' '));
        ui.dispatch.txt.appendChild(node('b', 'bs-ship__cd', countdownLabel(t.countdown)));
      }
    } else {
      ui.dispatch.txt.appendChild(document.createTextNode(LABEL_SHIP + ' ' + t.ship));
      // nedělní svoz PPL: pás mluví o běžné expedici skladu (pondělí), rychlý
      // dopravce odesílá dřív — jen s dopravcem v nabídce produktu
      if (t.fast && t.fast.shipDiffers && fastOffered(t, ui.names)) {
        ui.dispatch.txt.appendChild(document.createTextNode(' · '));
        ui.dispatch.txt.appendChild(
          node('b', 'bs-ship__fastship', t.fast.tag + ' ' + LABEL_FAST_SHIP + ' ' + t.fast.ship)
        );
      }
    }
    ui.dispatch.zone.classList.toggle('is-today', !!t.shipsToday);
    return t;
  }

  function fill(ui, data) {
    ui.body.textContent = '';
    if (!data || !data.rows || !data.rows.length) {
      ui.body.appendChild(node('p', shellClass(PREFIX, 'msg'), LABEL_ERROR));
      return;
    }

    ui.dispatch = buildDispatch();
    ui.data = data;
    ui.names = data.rows.map(function (r) {
      return r.name;
    });
    const t = renderDispatch(ui);
    ui.body.appendChild(ui.dispatch.zone);
    // okno zvláštního závozu (deliveryTimeline.js `t.fast`) × dopravce
    // v nabídce produktu — stav si tick pamatuje, aby při překlopení (pátek
    // 14:30, sobota 24:00) překreslil skupiny
    ui.fastKey = fastKeyFor(t, ui.names);

    // Marketingová věta ke zvláštnímu závozu (GLS sobota / PPL neděle) — jen
    // v okně a jen když je dopravce u produktu v nabídce (nadrozměr ji nedostane).
    if (ui.fastKey) {
      const promo = node('p', 'bs-ship__fast', fastPromo(t));
      promo.insertBefore(icon('truck'), promo.firstChild);
      ui.body.appendChild(promo);
    }

    // Nabídka spadla na samotný nadrozměr → vysvětlující pás jako v pokladně,
    // zákazník jinak neví, proč výdejní místa i běžná doprava zmizely (#225).
    // Znění je product-scoped — nabídka od 2026-09-01 nezávisí na košíku.
    if (isOnlyOversize(data.rows)) {
      const note = node('p', 'bs-ship__oversize', OVERSIZE_NOTE_PRODUCT);
      note.insertBefore(icon('truck'), note.firstChild);
      ui.body.appendChild(note);
    }

    // hmotnost produktu a pásmo ceníku — proč zákazník čte „od 109 Kč"
    const weightLine = buildWeightLine(data.rows);
    if (weightLine) ui.body.appendChild(weightLine);

    const groups = node('div', 'bs-ship__groups');
    SHIP_GROUPS.forEach(function (def) {
      const rows = data.rows.filter(function (r) {
        return r.group === def.key;
      });
      if (!rows.length) return;
      groups.appendChild(buildGroup(def, rows, t));
    });
    ui.body.appendChild(groups);

    // Věty pásu mluví o výdejním místě (FREE_SHIP_SCOPE) — bez skupiny
    // výdejních míst v nabídce PRODUKTU by lhaly (nadrozměr, #225), pás se
    // nekreslí. Čísla jdou z dataLayeru `cartInfo` (sdílený pás benefitBand.js,
    // stejná čísla jako košík); košík s nadrozměrem práh nenese → taky bez pásu.
    const hasBox = data.rows.some(function (r) {
      return r.group === 'box';
    });
    const free = hasBox ? freeShippingBand() : null;
    if (free) ui.body.appendChild(free);

    if (data.note) ui.body.appendChild(node('p', 'bs-ship__note', data.note));

    clearInterval(tick);
    tick = setInterval(function () {
      if (!modal.isOpen()) return;
      const now = renderDispatch(ui);
      // překlopení okna (pátek 14:30 GLS → PPL, sobota 24:00 PPL → nic) mění
      // termíny všech skupin i větu nad nimi — nejjednodušší je postavit tělo znovu
      if (fastKeyFor(now, ui.names) !== ui.fastKey) {
        fill(ui, ui.data);
        return;
      }
      const pick = ui.body.querySelector('.bs-ship__pickstatus');
      if (pick) renderPickupStatus(pick);
      // přelom otevírací doby mění i nejbližší den vyzvednutí
      const pickEta = ui.body.querySelector('[data-bs-ship-group="pickup"] .bs-ship__grpeta');
      if (pickEta && PICKUP_GROUP) renderEta(pickEta, PICKUP_GROUP, null, null);
    }, TICK_MS);
  }

  // --- otevření --------------------------------------------------------------

  function open(href) {
    const ui = modal.open();
    if (!ui) return;
    // loga dopravců se standardně tahají až po `load` (#313) — modal se ale
    // otevírá klikem a nesmí na to čekat; `ensureLogos()` je idempotentní
    ensureLogos();
    ui.body.appendChild(node('p', shellClass(PREFIX, 'msg'), LABEL_LOADING));
    loadShippingOptions(href).then(function (data) {
      if (modal.isOpen()) fill(ui, data);
    });
  }

  // --- napojení -------------------------------------------------------------

  document.addEventListener(
    'click',
    function (e) {
      const target = e.target instanceof Element ? e.target.closest('a.shipping-options') : null;
      if (!target) return;
      // na file:// (offline fixtures) i bez fetch necháváme nativní chování
      if (!window.fetch || location.protocol === 'file:') return;
      e.preventDefault();
      e.stopPropagation();
      open(target.getAttribute('href'));
    },
    true
  );

  // Změna košíku nabídku metod neovlivní (fetch je product-scoped, bez cookies)
  // a pás dopravy zdarma se čte z dataLayeru živě při otevření — invalidace
  // cache při ShoptetCartUpdated tím pádem zanikla.
}
